import type { AllPossibleSettingsKeys, Regions } from '../../types'
import { ColorScheme, DisplayUeopt } from '../constants/enums'
import { decomposePowerOfTwo } from '../utilities/decomposePowerOfTwo'
import { FCTYPE_UEOPT } from './mfcConstants'

export const enum GridDensityUeopt {
    small = 1,
    medium = 2,
    large = 3,
}

const enabledByDefault = {
    on: {
        arg: 32,
        param: 0,
    },
    off: {
        arg: 0,
        param: 1,
    },
}

const disabledByDefault = {
    off: {
        arg: 32,
        param: 0,
    },
    on: {
        arg: 0,
        param: 1,
    },
}

export const getNextDisplayType = (currentDisplayType: DisplayUeopt) => {
    switch (currentDisplayType) {
        case DisplayUeopt.text:
            return DisplayUeopt.default
        case DisplayUeopt.detailed:
            return DisplayUeopt.text
        case DisplayUeopt.preview:
            return DisplayUeopt.detailed
        case DisplayUeopt.default:
        default:
            return DisplayUeopt.preview
    }
}

export const USEREXT_NUM = 0
export const USEREXT_STRING = 1
export const USEREXT_DATA = 2
export const USEREXT_STAMP = 3
export const USEREXT_REMOVE = 32

export const UEOPT_ALLOW_USER_IMAGES = 3
export const UEOPT_ALLOW_USER_IMAGES_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

export const UEOPT_EXTRA_MENU_MODE = 24
export const UEOPT_EXTRA_MENU_MODE_STATE = {
    recommend: { arg: 1, param: 'recommend' },
    popularRooms: { arg: 1, param: 'popular_rooms' },
    trendingRooms: { arg: 1, param: 'trending_rooms' },
    recentlyVisited: { arg: 1, param: 'model_history' },
    newModels: { arg: 1, param: 'new_models' },
    missMfc: { arg: 1, param: 'miss_mfc' },
    tagsTopics: { arg: 1, param: 'tags_topics' },
    customSelection: { arg: 1, param: 'custom_alerts' },
} as const

export const UEOPT_ROOM_INLINE_PM = 37
export const UEOPT_ROOM_INLINE_PM_STATE = {
    onlyChatCommand: { arg: 32 },
    onlyRoomModel: { arg: 0, param: 1 },
    all: { arg: 0, param: 3 },
} as const

export const UEOPT_HIDE_VIDEO_MODEL_ROOM = 57
export const UEOPT_HIDE_VIDEO_MODEL_ROOM_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

export const UEOPT_HIGHLIGHT_FRIENDS_CHAT = 62
export const UEOPT_HIGHLIGHT_FRIENDS_CHAT_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

export const UEOPT_PRIMARY_RECOMMEND_MODEL = 74
export const UEOPT_PRIMARY_RECOMMEND_MODEL_STATE = {
    on: { arg: 0 },
} as const

export const UEOPT_SHOW_CHAT_HEADER_TOPIC = 85
export const UEOPT_SHOW_CHAT_HEADER_TOPIC_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

export const UEOPT_NO_EMOTE_PREVIEW = 94
export const UEOPT_NO_EMOTE_PREVIEW_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

export const UEOPT_HIDE_CHAT = 129
export const UEOPT_HIDE_CHAT_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

export const UEOPT_MODEL_LIST_OVERLAY_AVATAR_HOMEPAGE = 141
export const UEOPT_MODEL_LIST_OVERLAY_AVATAR_HOMEPAGE_STATE = {
    on: { arg: 32, param: 1 },
    off: { arg: 0, param: 0 },
} as const

export const UEOPT_PM_NOTIFICATION = 154
export const UEOPT_PM_NOTIFICATION_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

export const UEOPT_DISABLE_IN_CHAT_QUICKBUY = 161
export const UEOPT_DISABLE_IN_CHAT_QUICKBUY_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

export const UEOPT_HIGHLIGHT_CHAT_USERS = 171
export const UEOPT_HIGHLIGHT_CHAT_USERS_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

export const UEOPT_FONT_SIZE_CHAT = 176
export const UEOPT_FONT_SIZE_CHAT_STATE = {
    xSmall: { arg: 1, param: 'x-small' },
    small: { arg: 1, param: 'small' },
    normal: { arg: 32, param: 'normal' },
    large: { arg: 1, param: 'large' },
    xLarge: { arg: 1, param: 'x-large' },
} as const

export const UEOPT_PLAYER_VIDEO_LOCK_SIZE = 187
export const UEOPT_PLAYER_VIDEO_LOCK_SIZE_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

export const UEOPT_HIGHLIGHT_LATEST_USER_CHAT = 196
export const UEOPT_HIGHLIGHT_LATEST_USER_CHAT_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

export const UEOPT_LOUNGE_OPTIONS = 905
export const UEOPT_LOUNGE_OPTIONS_STATE = {
    off: { arg: 32, param: 0 },
    autoClick: { arg: 0, param: 1 },
    noPreviews: { arg: 0, param: 2 },
    autoClickAndNoPreviews: { arg: 0, param: 3 },
} as const

export const UEOPT_MUTE_GUESTS = 908
export const UEOPT_MUTE_GUESTS_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

export const UEOPT_MUTE_BASICS = 909
export const UEOPT_MUTE_BASICS_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

export const UEOPT_NEWS_NOTIFICATIONS = 910

export const UEOPT_PM_SOUND_ON = 919
export const UEOPT_PM_NOTIFICATION_SOUND = 920
export const UEOPT_RP_REVEAL_LEVEL = 921
export const UEOPT_RP_REVEAL_LEVEL_STATE = {
    hide: { arg: 32, param: 0 },
    approximation: { arg: 0, param: 1 },
    show: { arg: 0, param: 2 },
} as const

export const UEOPT_AUDIBLE_ALERT_MYWEBCAM_VIEWER = 80
export const UEOPT_AUDIBLE_ALERT_MODEL_BOOKMARKED_FRIEND_LOGIN = 83
export const UEOPT_AUDIBLE_ALERT_MODEL_FRIEND_LOGIN = 923
export const UEOPT_AUDIBLE_ALERT_MODEL_FRIEND_PRIVATE = 924
export const UEOPT_AUDIBLE_ALERT_USER_FRIEND_LOGIN = 926

export const UEOPT_TOKEN_REVEAL_LEVEL = 927
export const UEOPT_TOKEN_REVEAL_LEVEL_STATE = {
    hide: { arg: 32, param: 0 },
    approximation: { arg: 0, param: 1 },
    show: { arg: 0, param: 2 },
} as const

export const UEOPT_ROOM_TRUE_PRIVATE = 928
export const UEOPT_ROOM_TRUE_PRIVATE_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

// cam2cam
export const UEOPT_MYWEBCAM = 931
export const UEOPT_MYWEBCAM_OPTIONS = {
    everyone: { arg: 0, param: 0 },
    onlyModels: { arg: 32 },
    onlyFriendsList: { arg: 0, param: 2 },
    onlyFriendsListAndModels: { arg: 0, param: 4 },
    onlyAllowedViewers: { arg: 0, param: 5 },
    onlySpecifiedFriend: { arg: 0, param: 100 },
} as const

export const UEOPT_CHAT_COLOR = 935

export const UEOPT_ADMINISTRATIVE_EMAILS = 943
export const UEOPT_ADMINISTRATIVE_EMAILS_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

export const UEOPT_PURCHASE_CONFIRMATION_EMAILS = 944
export const UEOPT_PURCHASE_CONFIRMATION_EMAILS_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

export const UEOPT_EXCLUSIVE_GALLERY_OPT_OUT = 952
export const UEOPT_EXCLUSIVE_GALLERY_OPT_OUT_STATE = {
    off: { arg: 32, param: 0 },
    on: { arg: 0, param: 1 },
} as const

// display settings for home screen
export const UEOPT_HOME_DISPLAY_TYPE = 1000
export const UEOPT_HOME_DISPLAY_TYPES = {
    avatars: {
        arg: 32,
        param: DisplayUeopt.default,
    },
    previews: {
        arg: 1,
        param: DisplayUeopt.preview,
    },
    detailed: {
        arg: 1,
        param: DisplayUeopt.detailed,
    },
    text: {
        arg: 1,
        param: DisplayUeopt.text,
    },
} as const
export const UEOPT_HOME_DISPLAY_REGION = 1004
export const UEOPT_HOME_DISPLAY_REGIONS = {
    all: { name: 'All', arg: 32, bit: 0 },
    AF: { name: 'Africa', bit: 1 },
    AS: { name: 'Asia/Pacific Islands', bit: 2 },
    AU: { name: 'Australia', bit: 4 },
    SA: { name: 'Central/S. America', bit: 8 },
    EU: { name: 'Europe/Russia', bit: 16 },
    NA: { name: 'North America', bit: 32 },
    UK: { name: 'United Kingdom', bit: 64 },
    UN: { name: 'Unknown Region', bit: 128 },
} as const
export const UEOPT_HOME_SHOW_BY_TYPE = 1005
export const UEOPT_HOME_SHOW_BY_TYPES = {
    custom: {
        arg: 32,
        param: 'custom',
    },
    mobileFeeds: {
        arg: 1,
        param: 'feeds',
    },
    publicChat: {
        arg: 1,
        param: 'public',
    },
    friends: {
        arg: 1,
        param: 'friends',
    },
    friendsAndBookmarks: {
        arg: 1,
        param: 'friends_bookmarks',
    },
    friendsAndNew: {
        arg: 1,
        param: 'friends_new',
    },
} as const
export const UEOPT_HOME_SORT_BY = 1006
export const UEOPT_HOME_SORT_BY_TYPES = {
    camScore: {
        arg: 32,
        param: 'rating',
    },
    numberOfPeople: {
        arg: 1,
        param: 'popular_room',
    },
    alphabetical: {
        arg: 1,
        param: 'alphabetical',
    },
    newestLoggedOn: {
        arg: 1,
        param: 'newest_login',
    },
    newestSignedUp: {
        arg: 1,
        param: 'creation_date',
    },
    missMFC: {
        arg: 1,
        param: 'miss_mfc',
    },
    random: {
        arg: 1,
        param: 'random',
    },
} as const

export const UEOPT_MODEL_LIST_SORT_RATE = 1012
export const UEOPT_MODEL_LIST_SORT_RATES = {
    quarterMinute: {
        arg: 0,
        param: 15,
    },
    halfMinute: {
        arg: 32,
        param: 30,
    },
    minute: {
        arg: 0,
        param: 60,
    },
    twoMinutes: {
        arg: 0,
        param: 120,
    },
    fiveMinutes: {
        arg: 0,
        param: 300,
    },
    tenMinutes: {
        arg: 0,
        param: 600,
    },
}
export const UEOPT_HOME_FEED_ANIMATION_SPEED = 1016
// mobile defines and uses 1,5,10,30,50,100,300,500,1000
// we simplify for user's sake
export const UEOPT_HOME_FEED_ANIMATION_RATE_SPEEDS = {
    static: {
        arg: 0,
        param: 1,
    },
    slow: {
        arg: 32,
        param: 10,
    },
    medium: {
        arg: 0,
        param: 100,
    },
    fast: {
        arg: 0,
        param: 10000,
    },
} as const

export const UEOPT_ENABLE_FLOATING_PREVIEW = 1021
export const UEOPT_ENABLE_FLOATING_PREVIEW_OPTIONS = {
    on: { arg: 32, param: 1 },
    off: { arg: 0, param: 0 },
} as const

export const UEOPT_HOME_GROUP_BY = 1022
export const UEOPT_HOME_GROUP_BY_OPTIONS = {
    none: { arg: 1, param: 'none' },
    state: { arg: 32, param: 'state' },
} as const

export const UEOPT_APP_ALERT_SETTINGS = 2000
export enum AlertType {
    ALL = 'all',
    OFF = 'off',
    STATUS = 'members',
    NEWS = 'news',
}
export const UEOPT_APP_ALERT_SETTINGS_TYPES = {
    [AlertType.ALL]: {
        arg: 32 as const,
        bit: 0 as const,
    },
    [AlertType.OFF]: { bit: 1 as const },
    [AlertType.STATUS]: { bit: 2 as const },
    [AlertType.NEWS]: { bit: 4 as const },
}

//
// app specific UEOPTs brought to you by Todd
//

// Model Grid Density
export const UEOPT_APP_GRID_DENSITY = 2001
export const UEOPT_APP_GRID_DENSITY_OPTIONS = {
    small: {
        param: GridDensityUeopt.small,
    },
    medium: {
        arg: 32,
        param: GridDensityUeopt.medium,
    },
    large: {
        param: GridDensityUeopt.large,
    },
} as const
// dark mode
export const UEOPT_APP_COLOR_SCHEME = 2005
export const UEOPT_APP_COLOR_SCHEME_OPTIONS = {
    off: {
        arg: 32,
        param: 0,
    },
    dark: {
        arg: 0,
        param: 1,
    },
    light: {
        arg: 0,
        param: 2,
    },
} as const

export const getColorSchemeUEOPT = (colorScheme: ColorScheme) => {
    switch (colorScheme) {
        case ColorScheme.dark:
            return UEOPT_APP_COLOR_SCHEME_OPTIONS.dark.param
        case ColorScheme.light:
            return UEOPT_APP_COLOR_SCHEME_OPTIONS.light.param
        case ColorScheme.system:
        default:
            return UEOPT_APP_COLOR_SCHEME_OPTIONS.off.param
    }
}

// lounge autoplay
export const UEOPT_APP_LOUNGE_AUTOPLAY = 2007
export const UEOPT_APP_LOUNGE_AUTOPLAY_OPTIONS = {
    on: {
        arg: 32,
        param: 0,
    },
    off: {
        arg: 0,
        param: 1,
    },
    half: {
        arg: 0,
        param: 2,
    },
    quarter: {
        arg: 0,
        param: 3,
    },
} as const

// available space under rooms
export const UEOPT_APP_ROOM_AVAILABLE_SPACE_VIEW = 2008
export const UEOPT_APP_ROOM_AVAILABLE_SPACE_VIEWS = {
    mfcshare: {
        arg: 32,
        param: 'mfcshare',
    },
    profile: {
        arg: 0,
        param: 'profile',
    },
    myml: {
        arg: 1,
        param: 'myml',
    },
    whiteboard: {
        arg: 2,
        param: 'whiteboard',
    },
    pm: {
        arg: 3,
        param: 'pm',
    },
    models: {
        arg: 4,
        param: 'models',
    },
    sharefeed: {
        arg: 6,
        param: 'sharefeed',
    },
    none: {
        arg: 5,
        param: 'none',
    },
} as const
export const UEOPT_HOME_FEED_ANIMATE_PREVIEWS = 2009
export const UEOPT_HOME_FEED_ANIMATE_PREVIEWS_OPTIONS = {
    ...disabledByDefault,
}

// avatar preview on scroll (the mobile home grid plays one tile's preview
// as the scroll settles)
export const UEOPT_ENABLE_AVATAR_SCROLL_PREVIEW = 2010
export const UEOPT_ENABLE_AVATAR_SCROLL_PREVIEW_OPTIONS = {
    on: { arg: 32, param: 1 },
    off: { arg: 0, param: 0 },
} as const

//
// "Pin to Top" specific UEOPTs; these are shared with www so do not change values
//
export const UEOPT_PIN_TO_TOP_FRIENDS = 911
export const UEOPT_PIN_TO_TOP_FRIENDS_OPTIONS = enabledByDefault
export const UEOPT_PIN_TO_TOP_BOOKMARKS = 43
export const UEOPT_PIN_TO_TOP_BOOKMARK_OPTIONS = enabledByDefault
export const UEOPT_PIN_TO_TOP_MODELS_YOU_MAY_LIKE = 44
export const UEOPT_PIN_TO_TOP_MODELS_YOU_MAY_LIKE_OPTIONS = enabledByDefault
export const UEOPT_PIN_TO_TOP_POPULAR_ROOMS = 49
export const UEOPT_PIN_TO_TOP_POPULAR_ROOMS_OPTIONS = disabledByDefault
export const UEOPT_PIN_TO_TOP_TRENDING_ROOMS = 77
export const UEOPT_PIN_TO_TOP_TRENDING_ROOMS_OPTIONS = disabledByDefault
export const UEOPT_PIN_TO_TOP_RECENTLY_VISITED_ROOMS = 50
export const UEOPT_PIN_TO_TOP_RECENTLY_VISITED_ROOMS_OPTIONS = disabledByDefault
export const UEOPT_PIN_TO_TOP_CUSTOM_SELECTION = 103
export const UEOPT_PIN_TO_TOP_CUSTOM_SELECTION_OPTIONS = disabledByDefault
export const UEOPT_PIN_TO_TOP_MODELS_WITH_NOTES = 114
export const UEOPT_PIN_TO_TOP_MODELS_WITH_NOTES_OPTIONS = disabledByDefault
export const UEOPT_PIN_TO_TOP_NEW_MODELS = 39
export const UEOPT_PIN_TO_TOP_NEW_MODELS_OPTIONS = disabledByDefault
export const UEOPT_PIN_TO_TOP_MISS_MFC = 58
export const UEOPT_PIN_TO_TOP_MISS_MFC_OPTIONS = disabledByDefault
export const UEOPT_PIN_TO_TOP_PAST_MISSMFC_WINNERS = 115
export const UEOPT_PIN_TO_TOP_PAST_MISSMFC_WINNER_OPTIONS = disabledByDefault
export const UEOPT_PIN_TO_TOP_GROUP_PRIVATE_SHOWS = 912
export const UEOPT_PIN_TO_TOP_GROUP_PRIVATE_SHOWS_OPTIONS = {
    none: {
        arg: 0,
        param: 1,
    },
    groups: {
        arg: 4,
        param: 1,
    },
    privates: {
        arg: 1,
        param: 1,
    },
    both: {
        arg: 5,
        param: 1,
    },
} as const

//
// "Display Amount" specific to "Pin to Top" UEOPTs; these are shared with www so do not change values
// Each default to 10 except for Miss MFC; which defaults to 100
//
export const MFC_CUSTOM_AMOUNT_VALUES = [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20, 30,
]
export const MFC_MISS_MFC_AMOUNT_VALUES = [10, 20, 50, 100, 250, 500, 1000]
export const PIN_TO_TOP_GENERIC_AMOUNT_OPTIONS = {
    none: {
        arg: 0,
        param: 0,
    },
    one: {
        arg: 0,
        param: 1,
    },
    two: {
        arg: 0,
        param: 2,
    },
    three: {
        arg: 0,
        param: 3,
    },
    four: {
        arg: 0,
        param: 4,
    },
    five: {
        arg: 0,
        param: 5,
    },
    six: {
        arg: 0,
        param: 6,
    },
    seven: {
        arg: 0,
        param: 7,
    },
    eight: {
        arg: 0,
        param: 9,
    },
    nine: {
        arg: 0,
        param: 9,
    },
    ten: {
        arg: 32,
        param: 10,
    },
    eleven: {
        arg: 0,
        param: 11,
    },
    twelve: {
        arg: 0,
        param: 12,
    },
    fiften: {
        arg: 0,
        param: 15,
    },
    twenty: {
        arg: 0,
        param: 20,
    },
    thirty: {
        arg: 0,
        param: 30,
    },
}
export const UEOPT_PIN_TO_TOP_CUSTOM_SELECTION_AMOUNT = 102
export const UEOPT_PIN_TO_TOP_CUSTOM_SELECTION_AMOUNT_OPTIONS =
    PIN_TO_TOP_GENERIC_AMOUNT_OPTIONS
export const UEOPT_PIN_TO_TOP_MODELS_YOU_MAY_LIKE_AMOUNT = 47
export const UEOPT_PIN_TO_TOP_MODELS_YOU_MAY_LIKE_AMOUNT_OPTIONS =
    PIN_TO_TOP_GENERIC_AMOUNT_OPTIONS
export const UEOPT_PIN_TO_TOP_POPULAR_ROOMS_AMOUNT = 48
export const UEOPT_PIN_TO_TOP_POPULAR_ROOMS_AMOUNT_OPTIONS =
    PIN_TO_TOP_GENERIC_AMOUNT_OPTIONS
export const UEOPT_PIN_TO_TOP_TRENDING_ROOMS_AMOUNT = 78
export const UEOPT_PIN_TO_TOP_TRENDING_ROOMS_AMOUNT_OPTIONS =
    PIN_TO_TOP_GENERIC_AMOUNT_OPTIONS
export const UEOPT_PIN_TO_TOP_RECENTLY_VISITED_AMOUNT = 51
export const UEOPT_PIN_TO_TOP_RECENTLY_VISITED_AMOUNT_OPTIONS =
    PIN_TO_TOP_GENERIC_AMOUNT_OPTIONS
export const UEOPT_PIN_TO_TOP_MISS_MFC_AMOUNT = 59
export const UEOPT_PIN_TO_TOP_MISS_MFC_AMOUNT_OPTIONS = {
    ten: { arg: 0, param: 10 },
    twenty: { arg: 0, param: 20 },
    fifty: { arg: 0, param: 50 },
    oneHundred: { arg: 32, param: 100 },
    twoHundredFifty: { arg: 0, param: 250 },
    fiveHundred: { arg: 0, param: 500 },
    oneThousand: { arg: 0, param: 1000 },
}

export const UEOPT_CONFIG = {
    // app specific
    [UEOPT_APP_ROOM_AVAILABLE_SPACE_VIEW]: {
        type: USEREXT_STRING,
        value: UEOPT_APP_ROOM_AVAILABLE_SPACE_VIEWS.mfcshare.param,
    },
    [UEOPT_APP_ALERT_SETTINGS]: {
        type: USEREXT_NUM,
        value: UEOPT_APP_ALERT_SETTINGS_TYPES.all.bit,
    },
    [UEOPT_APP_COLOR_SCHEME]: {
        type: USEREXT_NUM,
        value: UEOPT_APP_COLOR_SCHEME_OPTIONS.off.param,
    },
    [UEOPT_ENABLE_FLOATING_PREVIEW]: {
        type: USEREXT_NUM,
        value: UEOPT_ENABLE_FLOATING_PREVIEW_OPTIONS.on.param,
    },
    [UEOPT_ENABLE_AVATAR_SCROLL_PREVIEW]: {
        type: USEREXT_NUM,
        value: UEOPT_ENABLE_AVATAR_SCROLL_PREVIEW_OPTIONS.on.param,
    },
    [UEOPT_APP_GRID_DENSITY]: {
        type: USEREXT_NUM,
        value: UEOPT_APP_GRID_DENSITY_OPTIONS.medium.param,
    },
    [UEOPT_APP_LOUNGE_AUTOPLAY]: {
        type: USEREXT_NUM,
        value: UEOPT_APP_LOUNGE_AUTOPLAY_OPTIONS.on.param,
    },
    // shared between apps (mobile.mfc and app.mfc; occasionally www)
    [UEOPT_ADMINISTRATIVE_EMAILS]: {
        type: USEREXT_NUM,
        value: UEOPT_ADMINISTRATIVE_EMAILS_STATE.on.param,
    },
    [UEOPT_ALLOW_USER_IMAGES]: {
        type: USEREXT_NUM,
        value: UEOPT_ALLOW_USER_IMAGES_STATE.on.param,
    },
    [UEOPT_DISABLE_IN_CHAT_QUICKBUY]: {
        type: USEREXT_NUM,
        value: UEOPT_DISABLE_IN_CHAT_QUICKBUY_STATE.off.param,
    },
    [UEOPT_EXCLUSIVE_GALLERY_OPT_OUT]: {
        type: USEREXT_NUM,
        value: UEOPT_EXCLUSIVE_GALLERY_OPT_OUT_STATE.off.param,
    },
    [UEOPT_EXTRA_MENU_MODE]: {
        type: USEREXT_STRING,
        value: UEOPT_EXTRA_MENU_MODE_STATE.recommend.param,
    },
    [UEOPT_FONT_SIZE_CHAT]: {
        type: USEREXT_STRING,
        value: UEOPT_FONT_SIZE_CHAT_STATE.normal.param,
    },
    [UEOPT_HIDE_CHAT]: {
        type: USEREXT_NUM,
        value: UEOPT_HIDE_CHAT_STATE.off.param,
    },
    [UEOPT_HIDE_VIDEO_MODEL_ROOM]: {
        type: USEREXT_NUM,
        value: UEOPT_HIDE_VIDEO_MODEL_ROOM_STATE.off.param,
    },
    [UEOPT_HIGHLIGHT_CHAT_USERS]: {
        type: USEREXT_NUM,
        value: UEOPT_HIGHLIGHT_CHAT_USERS_STATE.off.param,
    },
    [UEOPT_HIGHLIGHT_FRIENDS_CHAT]: {
        type: USEREXT_NUM,
        value: UEOPT_HIGHLIGHT_FRIENDS_CHAT_STATE.off.param,
    },
    [UEOPT_HIGHLIGHT_LATEST_USER_CHAT]: {
        type: USEREXT_NUM,
        value: UEOPT_HIGHLIGHT_LATEST_USER_CHAT_STATE.off.param,
    },
    [UEOPT_HOME_DISPLAY_REGION]: {
        type: USEREXT_NUM,
        value: UEOPT_HOME_DISPLAY_REGIONS.all.bit,
    },
    [UEOPT_HOME_DISPLAY_TYPE]: {
        type: USEREXT_STRING,
        value: UEOPT_HOME_DISPLAY_TYPES.avatars.param,
    },
    [UEOPT_HOME_SHOW_BY_TYPE]: {
        type: USEREXT_STRING,
        value: UEOPT_HOME_SHOW_BY_TYPES.custom.param,
    },
    [UEOPT_HOME_SORT_BY]: {
        type: USEREXT_STRING,
        value: UEOPT_HOME_SORT_BY_TYPES.camScore.param,
    },
    [UEOPT_HOME_GROUP_BY]: {
        type: USEREXT_STRING,
        value: UEOPT_HOME_GROUP_BY_OPTIONS.state.param,
    },
    [UEOPT_HOME_FEED_ANIMATE_PREVIEWS]: {
        type: USEREXT_NUM,
        value: UEOPT_HOME_FEED_ANIMATE_PREVIEWS_OPTIONS.on.param,
    },
    [UEOPT_HOME_FEED_ANIMATION_SPEED]: {
        type: USEREXT_NUM,
        value: UEOPT_HOME_FEED_ANIMATION_RATE_SPEEDS.slow.param,
    },
    [UEOPT_LOUNGE_OPTIONS]: {
        type: USEREXT_NUM,
        value: UEOPT_LOUNGE_OPTIONS_STATE.off.param,
    },
    [UEOPT_MODEL_LIST_OVERLAY_AVATAR_HOMEPAGE]: {
        type: USEREXT_NUM,
        value: UEOPT_MODEL_LIST_OVERLAY_AVATAR_HOMEPAGE_STATE.on.param,
    },
    [UEOPT_MODEL_LIST_SORT_RATE]: {
        type: USEREXT_NUM,
        value: UEOPT_MODEL_LIST_SORT_RATES.halfMinute.param,
    },
    [UEOPT_MUTE_BASICS]: {
        type: USEREXT_NUM,
        value: UEOPT_MUTE_BASICS_STATE.off.param,
    },
    [UEOPT_MUTE_GUESTS]: {
        type: USEREXT_NUM,
        value: UEOPT_MUTE_GUESTS_STATE.off.param,
    },
    [UEOPT_MYWEBCAM]: {
        type: USEREXT_NUM,
        value: UEOPT_MYWEBCAM_OPTIONS.onlyModels.arg,
    },
    [UEOPT_NEWS_NOTIFICATIONS]: {
        type: USEREXT_NUM,
        value: 3,
    },
    [UEOPT_NO_EMOTE_PREVIEW]: {
        type: USEREXT_NUM,
        value: UEOPT_NO_EMOTE_PREVIEW_STATE.off.param,
    },
    [UEOPT_PIN_TO_TOP_FRIENDS]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_FRIENDS_OPTIONS.on.param,
    },
    [UEOPT_PIN_TO_TOP_BOOKMARKS]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_FRIENDS_OPTIONS.on.param,
    },
    [UEOPT_PIN_TO_TOP_MODELS_YOU_MAY_LIKE]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_FRIENDS_OPTIONS.off.param,
    },
    [UEOPT_PIN_TO_TOP_POPULAR_ROOMS]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_POPULAR_ROOMS_OPTIONS.off.param,
    },
    [UEOPT_PIN_TO_TOP_TRENDING_ROOMS]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_TRENDING_ROOMS_OPTIONS.off.param,
    },
    [UEOPT_PIN_TO_TOP_RECENTLY_VISITED_ROOMS]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_RECENTLY_VISITED_ROOMS_OPTIONS.off.param,
    },
    [UEOPT_PIN_TO_TOP_CUSTOM_SELECTION]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_CUSTOM_SELECTION_OPTIONS.off.param,
    },
    [UEOPT_PIN_TO_TOP_MODELS_WITH_NOTES]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_MODELS_WITH_NOTES_OPTIONS.off.param,
    },
    [UEOPT_PIN_TO_TOP_NEW_MODELS]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_NEW_MODELS_OPTIONS.off.param,
    },
    [UEOPT_PIN_TO_TOP_MISS_MFC]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_MISS_MFC_OPTIONS.off.param,
    },
    [UEOPT_PIN_TO_TOP_PAST_MISSMFC_WINNERS]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_PAST_MISSMFC_WINNER_OPTIONS.off.param,
    },
    [UEOPT_PIN_TO_TOP_GROUP_PRIVATE_SHOWS]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_GROUP_PRIVATE_SHOWS_OPTIONS.none.param,
    },
    [UEOPT_PIN_TO_TOP_CUSTOM_SELECTION_AMOUNT]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_CUSTOM_SELECTION_AMOUNT_OPTIONS.ten.param,
    },
    [UEOPT_PIN_TO_TOP_MODELS_YOU_MAY_LIKE_AMOUNT]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_MODELS_YOU_MAY_LIKE_AMOUNT_OPTIONS.ten.param,
    },
    [UEOPT_PIN_TO_TOP_POPULAR_ROOMS_AMOUNT]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_POPULAR_ROOMS_AMOUNT_OPTIONS.ten.param,
    },
    [UEOPT_PIN_TO_TOP_TRENDING_ROOMS_AMOUNT]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_TRENDING_ROOMS_AMOUNT_OPTIONS.ten.param,
    },
    [UEOPT_PIN_TO_TOP_RECENTLY_VISITED_AMOUNT]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_RECENTLY_VISITED_AMOUNT_OPTIONS.ten.param,
    },
    [UEOPT_PIN_TO_TOP_MISS_MFC_AMOUNT]: {
        type: USEREXT_NUM,
        value: UEOPT_PIN_TO_TOP_MISS_MFC_AMOUNT_OPTIONS.oneHundred.param,
    },
    [UEOPT_PLAYER_VIDEO_LOCK_SIZE]: {
        type: USEREXT_NUM,
        value: UEOPT_PLAYER_VIDEO_LOCK_SIZE_STATE.off.param,
    },
    [UEOPT_PM_NOTIFICATION]: {
        type: USEREXT_NUM,
        value: 0,
    },
    [UEOPT_PRIMARY_RECOMMEND_MODEL]: {
        type: USEREXT_STRING,
        value: undefined,
    },
    [UEOPT_PURCHASE_CONFIRMATION_EMAILS]: {
        type: USEREXT_NUM,
        value: UEOPT_PURCHASE_CONFIRMATION_EMAILS_STATE.on.param,
    },
    [UEOPT_ROOM_INLINE_PM]: {
        type: USEREXT_NUM,
        value: UEOPT_ROOM_INLINE_PM_STATE.onlyChatCommand.arg,
    },
    [UEOPT_ROOM_TRUE_PRIVATE]: {
        type: USEREXT_NUM,
        value: UEOPT_ROOM_TRUE_PRIVATE_STATE.off.arg,
    },
    [UEOPT_SHOW_CHAT_HEADER_TOPIC]: {
        type: USEREXT_NUM,
        value: UEOPT_SHOW_CHAT_HEADER_TOPIC_STATE.on.param,
    },
    [UEOPT_RP_REVEAL_LEVEL]: {
        type: USEREXT_NUM,
        value: UEOPT_RP_REVEAL_LEVEL_STATE.show.param,
    },
    [UEOPT_TOKEN_REVEAL_LEVEL]: {
        type: USEREXT_NUM,
        value: UEOPT_TOKEN_REVEAL_LEVEL_STATE.show.param,
    },
}

export type UeoptConfig = typeof UEOPT_CONFIG

export const UEOPT_HOME_DISPLAY_REGIONS_BY_BITS = {
    128: { id: 'UN', name: UEOPT_HOME_DISPLAY_REGIONS['UN'].name },
    64: { id: 'UK', name: UEOPT_HOME_DISPLAY_REGIONS['UK'].name },
    32: { id: 'NA', name: UEOPT_HOME_DISPLAY_REGIONS['NA'].name },
    16: { id: 'EU', name: UEOPT_HOME_DISPLAY_REGIONS['EU'].name },
    8: { id: 'SA', name: UEOPT_HOME_DISPLAY_REGIONS['SA'].name },
    4: { id: 'AU', name: UEOPT_HOME_DISPLAY_REGIONS['AU'].name },
    2: { id: 'AS', name: UEOPT_HOME_DISPLAY_REGIONS['AS'].name },
    1: { id: 'AF', name: UEOPT_HOME_DISPLAY_REGIONS['AF'].name },
} as const

/**
 * The eight home regions for a 1004 value, whose set bits are the HIDDEN
 * regions. A fresh object on every call: the constant above is never touched,
 * so earlier results don't change under their holders.
 */
export const regionBitMap = (value: number): Regions => {
    const regions = {} as Regions
    decomposePowerOfTwo(value, (bit, show) => {
        regions[bit] = { ...UEOPT_HOME_DISPLAY_REGIONS_BY_BITS[bit], show }
    })
    return regions
}

export const lookupUeoptById = <T extends AllPossibleSettingsKeys>(type: T) => {
    switch (type) {
        case 'availableSpaceView':
            return UEOPT_APP_ROOM_AVAILABLE_SPACE_VIEW
        // app specific
        case 'colorScheme':
            return UEOPT_APP_COLOR_SCHEME
        case 'gridDensity':
            return UEOPT_APP_GRID_DENSITY
        case 'loungeAutoplay':
            return UEOPT_APP_LOUNGE_AUTOPLAY
        // shared ueopts
        case 'administrativeEmails':
            return UEOPT_ADMINISTRATIVE_EMAILS
        case 'alerts':
            return UEOPT_APP_ALERT_SETTINGS
        case 'allowUserImages':
            return UEOPT_ALLOW_USER_IMAGES
        case 'animatePreviews':
            return UEOPT_HOME_FEED_ANIMATE_PREVIEWS
        case 'animationSpeed':
            return UEOPT_HOME_FEED_ANIMATION_SPEED
        case 'disableInChatQuickbuy':
            return UEOPT_DISABLE_IN_CHAT_QUICKBUY
        case 'display':
            return UEOPT_HOME_DISPLAY_TYPE
        case 'enableAvatarScrollPreview':
            return UEOPT_ENABLE_AVATAR_SCROLL_PREVIEW
        case 'enableFloatingPreview':
            return UEOPT_ENABLE_FLOATING_PREVIEW
        case 'extraMenuMode':
            return UEOPT_EXTRA_MENU_MODE
        case 'chatFontSize':
            return UEOPT_FONT_SIZE_CHAT
        case 'hideChat':
            return UEOPT_HIDE_CHAT
        case 'hideVideoModelRoom':
            return UEOPT_HIDE_VIDEO_MODEL_ROOM
        case 'highlightChatHover':
            return UEOPT_HIGHLIGHT_CHAT_USERS
        case 'highlightFriendsChat':
            return UEOPT_HIGHLIGHT_FRIENDS_CHAT
        case 'highlightLatestChat':
            return UEOPT_HIGHLIGHT_LATEST_USER_CHAT
        case 'groupBy':
            return UEOPT_HOME_GROUP_BY
        case 'inlinePM':
            return UEOPT_ROOM_INLINE_PM
        case 'loungeOptions':
            return UEOPT_LOUNGE_OPTIONS
        case 'modelListSortRate':
            return UEOPT_MODEL_LIST_SORT_RATE
        case 'muteBasics':
            return UEOPT_MUTE_BASICS
        case 'muteGuests':
            return UEOPT_MUTE_GUESTS
        case 'noEmotePreview':
            return UEOPT_NO_EMOTE_PREVIEW
        case 'options':
            return UEOPT_MYWEBCAM
        case 'overlayAvatarHomepage':
            return UEOPT_MODEL_LIST_OVERLAY_AVATAR_HOMEPAGE
        case 'pinFriends':
            return UEOPT_PIN_TO_TOP_FRIENDS
        case 'pinBookmarks':
            return UEOPT_PIN_TO_TOP_BOOKMARKS
        case 'pinTrendingRooms':
            return UEOPT_PIN_TO_TOP_TRENDING_ROOMS
        case 'pinPopularRooms':
            return UEOPT_PIN_TO_TOP_POPULAR_ROOMS
        case 'pinModelsYouMayLike':
            return UEOPT_PIN_TO_TOP_MODELS_YOU_MAY_LIKE
        case 'pinModelsWithNotes':
            return UEOPT_PIN_TO_TOP_MODELS_WITH_NOTES
        case 'pinNewModels':
            return UEOPT_PIN_TO_TOP_NEW_MODELS
        case 'pinMissMFC':
            return UEOPT_PIN_TO_TOP_MISS_MFC
        case 'pinPrivateGroupShows':
            return UEOPT_PIN_TO_TOP_GROUP_PRIVATE_SHOWS
        case 'pinRecentlyVisitedRooms':
            return UEOPT_PIN_TO_TOP_RECENTLY_VISITED_ROOMS
        case 'pinCustomSelection':
            return UEOPT_PIN_TO_TOP_CUSTOM_SELECTION
        case 'pinPastWinners':
            return UEOPT_PIN_TO_TOP_PAST_MISSMFC_WINNERS
        case 'pinCustomSelectionAmount':
            return UEOPT_PIN_TO_TOP_CUSTOM_SELECTION_AMOUNT
        case 'pinModelsYouMayLikeAmount':
            return UEOPT_PIN_TO_TOP_MODELS_YOU_MAY_LIKE_AMOUNT
        case 'pinPopularRoomsAmount':
            return UEOPT_PIN_TO_TOP_POPULAR_ROOMS_AMOUNT
        case 'pinTrendingRoomsAmount':
            return UEOPT_PIN_TO_TOP_TRENDING_ROOMS_AMOUNT
        case 'pinRecentlyVisitedRoomsAmount':
            return UEOPT_PIN_TO_TOP_RECENTLY_VISITED_AMOUNT
        case 'pinMissMFCAmount':
            return UEOPT_PIN_TO_TOP_MISS_MFC_AMOUNT
        case 'primaryRecommendedModel':
            return UEOPT_PRIMARY_RECOMMEND_MODEL
        case 'privateGalleryOptOut':
            return UEOPT_EXCLUSIVE_GALLERY_OPT_OUT
        case 'purchaseConfirmationEmails':
            return UEOPT_PURCHASE_CONFIRMATION_EMAILS
        case 'region':
            return UEOPT_HOME_DISPLAY_REGION
        case 'rpRevealLevel':
            return UEOPT_RP_REVEAL_LEVEL
        case 'show':
            return UEOPT_HOME_SHOW_BY_TYPE
        case 'showChatHeaderTopic':
            return UEOPT_SHOW_CHAT_HEADER_TOPIC
        case 'sort':
            return UEOPT_HOME_SORT_BY
        case 'tokenRevealLevel':
            return UEOPT_TOKEN_REVEAL_LEVEL
        case 'truePrivate':
            return UEOPT_ROOM_TRUE_PRIVATE
        case 'pmNotification':
            return UEOPT_PM_NOTIFICATION
        case 'videoSizeLocked':
            return UEOPT_PLAYER_VIDEO_LOCK_SIZE
        default:
            return undefined
    }
}

export const ueoptMap = {
    [UEOPT_APP_ALERT_SETTINGS]: 'alerts',
    [UEOPT_APP_COLOR_SCHEME]: 'colorScheme',
    [UEOPT_APP_GRID_DENSITY]: 'gridDensity',
    [UEOPT_APP_LOUNGE_AUTOPLAY]: 'loungeAutoplay',
    [UEOPT_APP_ROOM_AVAILABLE_SPACE_VIEW]: 'availableSpaceView',
    [UEOPT_ADMINISTRATIVE_EMAILS]: 'administrativeEmails',
    [UEOPT_ALLOW_USER_IMAGES]: 'allowUserImages',
    [UEOPT_AUDIBLE_ALERT_MYWEBCAM_VIEWER]: 'audibleAlertMyWebcamWatcherJoined',
    [UEOPT_AUDIBLE_ALERT_MODEL_BOOKMARKED_FRIEND_LOGIN]:
        'audibleAlertBookmarkedModelFriendLogin',
    [UEOPT_AUDIBLE_ALERT_MODEL_FRIEND_LOGIN]: 'audibleAlertModelFriendLogin',
    [UEOPT_AUDIBLE_ALERT_MODEL_FRIEND_PRIVATE]:
        'audibleAlertModelEntersPrivateOrGroup',
    [UEOPT_AUDIBLE_ALERT_USER_FRIEND_LOGIN]: 'audibleAlertNonModelFriendLogin',
    [UEOPT_CHAT_COLOR]: 'chatFontColor',
    [UEOPT_DISABLE_IN_CHAT_QUICKBUY]: 'disableInChatQuickbuy',
    [UEOPT_ENABLE_AVATAR_SCROLL_PREVIEW]: 'enableAvatarScrollPreview',
    [UEOPT_ENABLE_FLOATING_PREVIEW]: 'enableFloatingPreview',
    [UEOPT_EXCLUSIVE_GALLERY_OPT_OUT]: 'privateGalleryOptOut',
    [UEOPT_EXTRA_MENU_MODE]: 'extraMenuMode',
    [UEOPT_FONT_SIZE_CHAT]: 'chatFontSize',
    [UEOPT_HIDE_CHAT]: 'hideChat',
    [UEOPT_HIDE_VIDEO_MODEL_ROOM]: 'hideVideoModelRoom',
    [UEOPT_HIGHLIGHT_CHAT_USERS]: 'highlightChatHover',
    [UEOPT_HIGHLIGHT_FRIENDS_CHAT]: 'highlightFriendsChat',
    [UEOPT_HIGHLIGHT_LATEST_USER_CHAT]: 'highlightLatestChat',
    [UEOPT_HOME_DISPLAY_REGION]: 'region',
    [UEOPT_HOME_DISPLAY_TYPE]: 'display',
    [UEOPT_HOME_FEED_ANIMATE_PREVIEWS]: 'animatePreviews',
    [UEOPT_HOME_FEED_ANIMATION_SPEED]: 'animationSpeed',
    [UEOPT_HOME_GROUP_BY]: 'groupBy',
    [UEOPT_HOME_SORT_BY]: 'sort',
    [UEOPT_HOME_SHOW_BY_TYPE]: 'show',
    [UEOPT_LOUNGE_OPTIONS]: 'loungeOptions',
    [UEOPT_MODEL_LIST_OVERLAY_AVATAR_HOMEPAGE]: 'overlayAvatarHomepage',
    [UEOPT_MODEL_LIST_SORT_RATE]: 'modelListSortRate',
    [UEOPT_MUTE_BASICS]: 'muteBasics',
    [UEOPT_MUTE_GUESTS]: 'muteGuests',
    [UEOPT_MYWEBCAM]: 'options',
    [UEOPT_NO_EMOTE_PREVIEW]: 'noEmotePreview',
    [UEOPT_PIN_TO_TOP_FRIENDS]: 'pinFriends',
    [UEOPT_PIN_TO_TOP_BOOKMARKS]: 'pinBookmarks',
    [UEOPT_PIN_TO_TOP_TRENDING_ROOMS]: 'pinTrendingRooms',
    [UEOPT_PIN_TO_TOP_POPULAR_ROOMS]: 'pinPopularRooms',
    [UEOPT_PIN_TO_TOP_MODELS_YOU_MAY_LIKE]: 'pinModelsYouMayLike',
    [UEOPT_PIN_TO_TOP_MODELS_WITH_NOTES]: 'pinModelsWithNotes',
    [UEOPT_PIN_TO_TOP_NEW_MODELS]: 'pinNewModels',
    [UEOPT_PIN_TO_TOP_MISS_MFC]: 'pinMissMFC',
    [UEOPT_PIN_TO_TOP_GROUP_PRIVATE_SHOWS]: 'pinPrivateGroupShows',
    [UEOPT_PIN_TO_TOP_RECENTLY_VISITED_ROOMS]: 'pinRecentlyVisitedRooms',
    [UEOPT_PIN_TO_TOP_CUSTOM_SELECTION]: 'pinCustomSelection',
    [UEOPT_PIN_TO_TOP_PAST_MISSMFC_WINNERS]: 'pinPastWinners',
    [UEOPT_PIN_TO_TOP_CUSTOM_SELECTION_AMOUNT]: 'pinCustomSelectionAmount',
    [UEOPT_PIN_TO_TOP_MODELS_YOU_MAY_LIKE_AMOUNT]: 'pinModelsYouMayLikeAmount',
    [UEOPT_PIN_TO_TOP_POPULAR_ROOMS_AMOUNT]: 'pinPopularRoomsAmount',
    [UEOPT_PIN_TO_TOP_TRENDING_ROOMS_AMOUNT]: 'pinTrendingRoomsAmount',
    [UEOPT_PIN_TO_TOP_RECENTLY_VISITED_AMOUNT]: 'pinRecentlyVisitedRoomsAmount',
    [UEOPT_PIN_TO_TOP_MISS_MFC_AMOUNT]: 'pinMissMFCAmount',
    [UEOPT_PLAYER_VIDEO_LOCK_SIZE]: 'videoSizeLocked',
    [UEOPT_PM_SOUND_ON]: 'pmPlaySoundsOn',
    [UEOPT_PM_NOTIFICATION]: 'pmNotification',
    [UEOPT_PM_NOTIFICATION_SOUND]: 'pmNotificationSound',
    [UEOPT_PRIMARY_RECOMMEND_MODEL]: 'primaryRecommendedModel',
    [UEOPT_PURCHASE_CONFIRMATION_EMAILS]: 'purchaseConfirmationEmails',
    [UEOPT_ROOM_INLINE_PM]: 'inlinePM',
    [UEOPT_ROOM_TRUE_PRIVATE]: 'truePrivate',
    [UEOPT_RP_REVEAL_LEVEL]: 'rpRevealLevel',
    [UEOPT_SHOW_CHAT_HEADER_TOPIC]: 'showChatHeaderTopic',
    [UEOPT_TOKEN_REVEAL_LEVEL]: 'tokenRevealLevel',
} as const

type UeoptPayload = {
    type: number
    to: number
    arg1: number
    arg2: number
    payload: any
}

export const buildUeoptPayload = <T extends keyof UeoptConfig>(
    ueoptId: T,
    value: UeoptConfig[T]['value'],
): UeoptPayload => {
    const {
        [ueoptId]: { value: default_value = 0, type = USEREXT_NUM },
    } = UEOPT_CONFIG

    const data = {
        type: FCTYPE_UEOPT,
        to: 0,
        arg1: ueoptId,
        arg2: value === default_value ? USEREXT_REMOVE : type,
        payload: value,
    }

    if (ueoptId === UEOPT_HOME_DISPLAY_TYPE) {
        data.arg2 = type
    }

    // cam2cam specific
    if (ueoptId === UEOPT_MYWEBCAM && value === default_value) {
        data.arg2 = null
    }

    if (ueoptId === UEOPT_PRIMARY_RECOMMEND_MODEL) {
        if (value) data.arg2 = 0
        else data.arg2 = USEREXT_REMOVE
    }

    if (ueoptId === UEOPT_APP_COLOR_SCHEME) {
        if (value === ColorScheme.system) {
            data.payload = 0
            data.arg2 = USEREXT_REMOVE
        } else {
            if (value === ColorScheme.dark)
                data.payload = UEOPT_APP_COLOR_SCHEME_OPTIONS.dark.param
            if (value === ColorScheme.light)
                data.payload = UEOPT_APP_COLOR_SCHEME_OPTIONS.light.param
        }
    }

    if (
        ueoptId === UEOPT_ENABLE_FLOATING_PREVIEW ||
        ueoptId === UEOPT_ENABLE_AVATAR_SCROLL_PREVIEW
    ) {
        if (Boolean(value) === false) {
            data.payload = 0
            data.arg2 = 0
        } else {
            data.payload = 1
            data.arg2 = USEREXT_REMOVE
        }
    }

    // animate previews
    if (ueoptId === UEOPT_HOME_FEED_ANIMATE_PREVIEWS) {
        if (value === 1) {
            data.payload = 0
            data.arg2 = USEREXT_REMOVE
        } else {
            data.payload = 1
            data.arg2 = 0
        }
    }

    // truePrivate specific
    if (ueoptId === UEOPT_ROOM_TRUE_PRIVATE) {
        if (!value) {
            data.payload = null
            data.arg2 = USEREXT_REMOVE
        } else {
            data.payload = 1
        }
    }

    // inline PM specific
    if (ueoptId === UEOPT_ROOM_INLINE_PM) {
        data.payload = value === default_value ? null : value
    }

    // pin to top - on by default
    if (
        ueoptId === UEOPT_PIN_TO_TOP_MODELS_YOU_MAY_LIKE ||
        ueoptId === UEOPT_PIN_TO_TOP_FRIENDS ||
        ueoptId === UEOPT_PIN_TO_TOP_BOOKMARKS ||
        ueoptId === UEOPT_PIN_TO_TOP_CUSTOM_SELECTION
    ) {
        if (value === 0) {
            data.payload = 0
            data.arg2 = 0
        } else {
            data.payload = 32
            data.arg2 = null
        }
    }

    // pin to top - off by default
    // and custom amounts
    if (
        ueoptId === UEOPT_PIN_TO_TOP_TRENDING_ROOMS ||
        ueoptId === UEOPT_PIN_TO_TOP_NEW_MODELS ||
        ueoptId === UEOPT_PIN_TO_TOP_MISS_MFC ||
        ueoptId === UEOPT_PIN_TO_TOP_PAST_MISSMFC_WINNERS ||
        ueoptId === UEOPT_PIN_TO_TOP_POPULAR_ROOMS ||
        ueoptId === UEOPT_PIN_TO_TOP_RECENTLY_VISITED_ROOMS ||
        ueoptId === UEOPT_PIN_TO_TOP_TRENDING_ROOMS_AMOUNT ||
        ueoptId === UEOPT_PIN_TO_TOP_POPULAR_ROOMS_AMOUNT ||
        ueoptId === UEOPT_PIN_TO_TOP_MISS_MFC_AMOUNT ||
        ueoptId === UEOPT_PIN_TO_TOP_RECENTLY_VISITED_AMOUNT ||
        ueoptId === UEOPT_PIN_TO_TOP_CUSTOM_SELECTION_AMOUNT
    ) {
        data.payload = value === default_value ? null : value
    }

    if (ueoptId === UEOPT_PIN_TO_TOP_GROUP_PRIVATE_SHOWS) {
        data.arg2 = 1
    }

    if (ueoptId === UEOPT_EXTRA_MENU_MODE) {
        data.arg2 = 1
    }

    return data
}

export const getGroupPrivatePinToTopUeopt = (options: {
    groupShows?: boolean
    privateShows?: boolean
}): number => {
    const { groupShows = false, privateShows = false } = options
    if (!groupShows && !privateShows)
        return UEOPT_PIN_TO_TOP_GROUP_PRIVATE_SHOWS_OPTIONS.none.arg
    if (!groupShows && privateShows)
        return UEOPT_PIN_TO_TOP_GROUP_PRIVATE_SHOWS_OPTIONS.privates.arg
    if (groupShows && !privateShows)
        return UEOPT_PIN_TO_TOP_GROUP_PRIVATE_SHOWS_OPTIONS.groups.arg
    if (groupShows && privateShows)
        return UEOPT_PIN_TO_TOP_GROUP_PRIVATE_SHOWS_OPTIONS.both.arg
    return undefined
}

export const PM_NOTIFICATION_SOUND_TYPE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8]

/**
 * Used with UE API
 */
export const enum USER_EXTENSIONS {
    bookmarkedModelFriendLoginSound = 'sound_model_bookmarked_friend_login',
    modelFriendEnteredPrivateSound = 'sound_model_friend_private',
    modelFriendLoginSound = 'sound_model_friend_login',
    myWebCamNewViewerSound = 'sound_new_mywebcam_viewer',
    pmNotificationSound = 'pm_sound_type',
    pmNotificationSoundMode = 'pm_sound_mode',
    userFriendLoginSound = 'sound_user_friend_login',
    webcamSelectionMask = 'webcam_selection_mask',
}
